import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ErrorBar,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import { Card, Flex, Text } from '../../../Components'
import {
  CHART_ACTIVE_DOT,
  CHART_ANIMATION,
  CHART_ANIMATION_FAST,
  CHART_AXIS_LINE,
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_LEGEND_STYLE,
  CHART_MARGIN,
  CHART_TOOLTIP_STYLE
} from '../../../Components/Charts/chartTheme'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { formatDayKeyLabel, formatMs, getChartColor } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

/** Numero massimo di unit evidenziate nei grafici (stack + linee) */
const MAX_DISPLAY_UNITS = 14

/**
 * Risolve la chiave giorno da un bucket API
 * @param {string|Date} bucket - Bucket giornaliero o data
 * @returns {string} YYYY-MM-DD
 */
function dayKeyFromBucket(bucket) {
  if (typeof bucket === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(bucket)) return bucket
  return new Date(bucket).toISOString().slice(0, 10)
}

/**
 * Seleziona le unit con piu step nel periodo
 * @param {Array<{ unit: string, count?: number }>} series
 * @param {number} maxUnits
 * @returns {string[]}
 */
function pickTopUnitsByVolume(series, maxUnits) {
  const totals = {}
  for (const p of series) {
    if (!p.unit) continue
    totals[p.unit] = (totals[p.unit] || 0) + (p.count || 0)
  }
  return Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxUnits)
    .map(([u]) => u)
}

/**
 * Step per giorno e per unit (stack): top unit separate, resto in "altre"
 * @param {Array<{ bucket: string|Date, unit: string, count: number }>} series
 * @param {string[]} displayUnits
 * @returns {Array<Record<string, unknown>>}
 */
function buildStackedUnitDayRows(series, displayUnits) {
  const top = new Set(displayUnits)
  const byDay = {}

  for (const point of series) {
    const dayKey = dayKeyFromBucket(point.bucket)
    if (!byDay[dayKey]) {
      byDay[dayKey] = { nameSort: dayKey, name: formatDayKeyLabel(dayKey) }
      for (const u of displayUnits) byDay[dayKey][u] = 0
      byDay[dayKey].altre = 0
    }
    const row = byDay[dayKey]
    const c = point.count || 0
    if (top.has(point.unit)) row[point.unit] = (row[point.unit] || 0) + c
    else row.altre += c
  }

  return Object.values(byDay).sort((a, b) => a.nameSort.localeCompare(b.nameSort))
}

/**
 * Medie e σ per le unit in evidenza (stesso schema di prima)
 * @param {Array<{ bucket: string|Date, unit: string, count: number, avg: number, stdDev?: number }>} series
 * @param {string[]} displayUnits
 * @returns {Array<Record<string, unknown>>}
 */
function buildLineAvgRows(series, displayUnits) {
  const show = new Set(displayUnits)
  const byDay = {}

  for (const point of series) {
    const dayKey = dayKeyFromBucket(point.bucket)
    if (!byDay[dayKey]) {
      byDay[dayKey] = {
        nameSort: dayKey,
        name: formatDayKeyLabel(dayKey),
        totalCount: 0
      }
    }
    const row = byDay[dayKey]
    row.totalCount += point.count || 0

    if (show.has(point.unit)) {
      const std = Number.isFinite(point.stdDev) ? point.stdDev : 0
      row[`${point.unit}_avg`] = point.avg
      row[`${point.unit}_std`] = Math.max(0, std)
    }
  }

  return Object.values(byDay).sort((a, b) => a.nameSort.localeCompare(b.nameSort))
}

/**
 * Allinea righe stack allo stesso elenco di giorni (0 dove manca)
 * @param {string[]} sortedDays
 * @param {Array<Record<string, unknown>>} stackedRows
 * @param {string[]} displayUnits
 * @returns {Array<Record<string, unknown>>}
 */
function alignStackedToDays(sortedDays, stackedRows, displayUnits) {
  const map = Object.fromEntries(stackedRows.map((r) => [r.nameSort, r]))
  const keys = [...displayUnits, 'altre']
  return sortedDays.map((k) => {
    const src = map[k]
    const row = { nameSort: k, name: formatDayKeyLabel(k) }
    for (const key of keys) {
      row[key] = src?.[key] ?? 0
    }
    return row
  })
}

/**
 * Allinea serie medie agli stessi giorni
 * @param {string[]} sortedDays
 * @param {Array<Record<string, unknown>>} lineRows
 * @param {string[]} displayUnits
 * @returns {Array<Record<string, unknown>>}
 */
function alignLinesToDays(sortedDays, lineRows, displayUnits) {
  const map = Object.fromEntries(lineRows.map((r) => [r.nameSort, r]))
  return sortedDays.map((k) => {
    const ex = map[k]
    const row = { nameSort: k, name: formatDayKeyLabel(k), totalCount: ex?.totalCount ?? 0 }
    for (const u of displayUnits) {
      row[`${u}_avg`] = ex?.[`${u}_avg`]
      row[`${u}_std`] = ex?.[`${u}_std`] ?? 0
    }
    return row
  })
}

/**
 * Tooltip medie / σ
 * @param {Object} props
 * @param {boolean} props.active
 * @param {Array} props.payload
 * @param {string[]} units
 * @returns {JSX.Element|null}
 */
function LineOverviewTooltip({ active, payload, units }) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  return (
    <div style={{ ...CHART_TOOLTIP_STYLE, padding: '10px 14px', lineHeight: 1.6 }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--ink-900)' }}>{row.name}</div>
      <div style={{ marginBottom: 4, color: 'var(--ink-500)' }}>
        {t.pfStepNelGiorno} <strong style={{ color: 'var(--ink-900)' }}>{row.totalCount}</strong>
      </div>
      {units.map((u, i) => {
        const avg = row[`${u}_avg`]
        if (avg == null) return null
        const std = row[`${u}_std`] ?? 0
        const color = getChartColor(i)
        return (
          <div key={u} style={{ marginTop: 4, color }}>
            <span style={{ fontWeight: 600 }}>{u}</span>: {t.pfMedia} {formatMs(avg)} · σ{' '}
            {formatMs(std)}{' '}
            <span style={{ color: 'var(--ink-400)' }}>
              [{formatMs(Math.max(0, avg - std))} — {formatMs(avg + std)}]
            </span>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Tre grafici giornalieri globali: stack step per unit, medie tempi, process completati.
 * @param {{ startDate: Date, endDate: Date }} props
 * @returns {JSX.Element|null}
 */
export const AllUnitsDailyOverviewChart = ({ startDate, endDate }) => {
  const { data: timeSeries = [], isSuccess: tsOk } = useCQuery([
    'unitPerformanceTimeSeries',
    startDate,
    endDate,
    '__ALL__',
    'day'
  ])

  const { data: processDaily = [] } = useCQuery(['unitPerformanceProcessDaily', startDate, endDate])

  const { displayUnits, truncated, distinctUnitCount } = useMemo(() => {
    const displayUnitsInner = pickTopUnitsByVolume(timeSeries, MAX_DISPLAY_UNITS)
    const distinct = new Set(timeSeries.map((p) => p.unit).filter(Boolean))
    return {
      displayUnits: displayUnitsInner,
      truncated: distinct.size > displayUnitsInner.length,
      distinctUnitCount: distinct.size
    }
  }, [timeSeries])

  const allDayKeys = useMemo(() => {
    const s = new Set()
    for (const p of timeSeries) s.add(dayKeyFromBucket(p.bucket))
    for (const r of processDaily) s.add(r.day)
    return [...s].sort()
  }, [timeSeries, processDaily])

  const processCountByDay = useMemo(() => {
    const m = {}
    for (const r of processDaily) m[r.day] = r.count
    return m
  }, [processDaily])

  const stackedRaw = useMemo(
    () => buildStackedUnitDayRows(timeSeries, displayUnits),
    [timeSeries, displayUnits]
  )

  const lineRaw = useMemo(
    () => buildLineAvgRows(timeSeries, displayUnits),
    [timeSeries, displayUnits]
  )

  const stackedChartData = useMemo(
    () => alignStackedToDays(allDayKeys, stackedRaw, displayUnits),
    [allDayKeys, stackedRaw, displayUnits]
  )

  const lineChartData = useMemo(
    () => alignLinesToDays(allDayKeys, lineRaw, displayUnits),
    [allDayKeys, lineRaw, displayUnits]
  )

  const processBarData = useMemo(
    () =>
      allDayKeys.map((k) => ({
        name: formatDayKeyLabel(k),
        nameSort: k,
        processCount: processCountByDay[k] ?? 0
      })),
    [allDayKeys, processCountByDay]
  )

  const hasAltras = useMemo(() => stackedChartData.some((r) => r.altre > 0), [stackedChartData])

  if (!tsOk) return null

  const hasAnyDay = allDayKeys.length > 0
  const canShowUnitCharts = hasAnyDay && displayUnits.length > 0

  return (
    <div className="ncard" style={{ marginBottom: 18 }}>
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-chart-bar" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.pfVolumeProcessiPanoramica}</h3>
          <div className="ncard__sub">{t.pfTreViste}</div>
        </div>
      </div>
      <div className="ncard__body">
        {truncated && distinctUnitCount > 0 && (
          <Text
            value={t.pfGrafici12Prime
              .replace('${max}', MAX_DISPLAY_UNITS)
              .replace('${total}', distinctUnitCount)}
            size={11}
            style={{ color: 'var(--warning)', marginBottom: 12 }}
          />
        )}
        <Flex fw style={{ gap: 20 }}>
          <Card style={{ width: '100%' }}>
            <Text title bold value={t.pfStepPerUnitStack} size={15} style={{ marginBottom: 8 }} />
            <Text
              value={t.pfColonneImpilate}
              size={11}
              style={{ color: 'var(--ink-500)', marginBottom: 8 }}
            />
            <Flex fw style={{ height: 340 }}>
              {canShowUnitCharts ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stackedChartData} margin={CHART_MARGIN}>
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="name"
                      tick={CHART_AXIS_TICK}
                      axisLine={CHART_AXIS_LINE}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={CHART_AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Legend wrapperStyle={CHART_LEGEND_STYLE} iconType="circle" iconSize={8} />
                    {displayUnits.map((u, i) => (
                      <Bar
                        key={u}
                        dataKey={u}
                        name={u}
                        stackId="unitDay"
                        fill={getChartColor(i)}
                        maxBarSize={56}
                        radius={
                          i === displayUnits.length - 1 && !hasAltras ? [4, 4, 0, 0] : undefined
                        }
                        animationDuration={CHART_ANIMATION_FAST.duration}
                      />
                    ))}
                    {hasAltras ? (
                      <Bar
                        dataKey="altre"
                        name={t.pfAltreUnit}
                        stackId="unitDay"
                        fill="var(--ink-400)"
                        maxBarSize={56}
                        radius={[4, 4, 0, 0]}
                        animationDuration={CHART_ANIMATION_FAST.duration}
                      />
                    ) : null}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Flex fw fh style={{ justifyContent: 'center', alignItems: 'center' }}>
                  <Text
                    value={t.pfNessunDatoNelPeriodo}
                    size={14}
                    style={{ color: 'var(--ink-400)' }}
                  />
                </Flex>
              )}
            </Flex>
          </Card>

          <Card style={{ width: '100%' }}>
            <Text
              title
              bold
              value={t.pfTempoMedioEsecuzionePerUnit}
              size={15}
              style={{ marginBottom: 8 }}
            />
            <Text
              value={t.pfLineeMediaGiornaliera}
              size={11}
              style={{ color: 'var(--ink-500)', marginBottom: 8 }}
            />
            <Flex fw style={{ height: 340 }}>
              {canShowUnitCharts ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={lineChartData} margin={{ ...CHART_MARGIN, right: 16 }}>
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="name"
                      tick={CHART_AXIS_TICK}
                      axisLine={CHART_AXIS_LINE}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={CHART_AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v) => formatMs(v, true)}
                    />
                    <Tooltip content={<LineOverviewTooltip units={displayUnits} />} />
                    <Legend wrapperStyle={CHART_LEGEND_STYLE} iconType="circle" iconSize={8} />
                    {displayUnits.map((unit, i) => {
                      const color = getChartColor(i)
                      const avgKey = `${unit}_avg`
                      const stdKey = `${unit}_std`
                      return (
                        <Line
                          key={unit}
                          type="monotone"
                          dataKey={avgKey}
                          name={`${unit} (${t.pfMedia})`}
                          stroke={color}
                          strokeWidth={2.5}
                          dot={{ r: 3, strokeWidth: 2, stroke: 'var(--surface)', fill: color }}
                          activeDot={CHART_ACTIVE_DOT(color)}
                          connectNulls={false}
                          animationDuration={CHART_ANIMATION.duration}
                        >
                          <ErrorBar
                            dataKey={stdKey}
                            width={4}
                            stroke={color}
                            strokeWidth={1}
                            direction="y"
                          />
                        </Line>
                      )
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Flex fw fh style={{ justifyContent: 'center', alignItems: 'center' }}>
                  <Text
                    value={t.pfNessunDatoNelPeriodo}
                    size={14}
                    style={{ color: 'var(--ink-400)' }}
                  />
                </Flex>
              )}
            </Flex>
          </Card>

          <Card style={{ width: '100%' }}>
            <Text
              title
              bold
              value={t.pfProcessCompletatiPerGiorno}
              size={15}
              style={{ marginBottom: 8 }}
            />
            <Text
              value={t.pfConteggioProcessEndTime}
              size={11}
              style={{ color: 'var(--ink-500)', marginBottom: 8 }}
            />
            <Flex fw style={{ height: 280 }}>
              {hasAnyDay ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={processBarData} margin={CHART_MARGIN}>
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="name"
                      tick={CHART_AXIS_TICK}
                      axisLine={CHART_AXIS_LINE}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={CHART_AXIS_TICK}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      formatter={(value) => [value, t.pfProcess]}
                      labelStyle={{ fontWeight: 600, marginBottom: 2 }}
                      contentStyle={CHART_TOOLTIP_STYLE}
                    />
                    <Legend wrapperStyle={CHART_LEGEND_STYLE} iconType="circle" iconSize={8} />
                    <Bar
                      dataKey="processCount"
                      name={t.pfProcessCompletati}
                      fill="var(--primary)"
                      radius={[4, 4, 0, 0]}
                      maxBarSize={56}
                      animationDuration={600}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Flex fw fh style={{ justifyContent: 'center', alignItems: 'center' }}>
                  <Text
                    value={t.pfNessunGiornoNelRange}
                    size={14}
                    style={{ color: 'var(--ink-400)' }}
                  />
                </Flex>
              )}
            </Flex>
          </Card>
        </Flex>
      </div>
    </div>
  )
}
